import { action, observable, runInAction } from "mobx";
import { App } from "@capacitor/app";
import { DateTime } from "luxon";
import RootAppStore from "@state/stores/RootStore";
import { BankIdApiService } from "@api-services/integrations/BankIdApiService";
import { LoaderStore } from "@state/stores/common";
import { getRedirectOriginUrl } from "@utils/specific/capacitor";
import { StorageStateKeysEnum } from "@utils/storage";
import { BankIdSessionStatus } from "@utils/common/bankIdSessionStatus";
import { LoginType } from "@utils/common/loginTypeEnum";
import { BankIdFormData } from "@api-types/integrations";
import { localizationService } from "@state";
import { BankIdApiAuthDto } from "@api-types/integrations/BankIdApiAuthDto";
import { BankIdMvcService } from "@api-services/integrations/BankIdMvcService";
import { BankIdSessionStatusDto } from "@api-types/integrations/BankIdSessionStatusDto";

export default class BankIdCapacitorStore {
	@observable isBankIdFlowPopupActive: boolean = false;
	@observable isBankIdQrCodePopupActive: boolean = false;

	@observable capacitorFormData: BankIdFormData | null = null;
	@observable isAuthPending: boolean = false;
	@observable qrCodeImage: any | null = null; // state stores SVG from BE

	qrCodeData: BankIdApiAuthDto | null = null;

	authType: string;
	tokenTimeout: number = 120000; // 2 minutes = 120000 miliseconds

	maxPoolingDuration: number = 120000; // 2 minutes = 120000 miliseconds
	shouldStopSessionPolling: boolean = false;
	shouldStopQrPolling: boolean = false;

	sessionPollingInterval: number = 2000; // 2 seconds = 2000 miliseconds
	qrPollingInterval: number = 1500; // 1.5 seconds = 1500 miliseconds

	rootStore: RootAppStore;
	loaderStore: LoaderStore;

	constructor(rootStore: RootAppStore) {
		this.rootStore = rootStore;
		this.loaderStore = new LoaderStore();
	}

	@action.bound
	setIsAuthPending(isAuthPending: boolean) {
		this.isAuthPending = isAuthPending;
	}

	@action.bound
	activateFlowPopup(authType?: string) {
		this.isBankIdFlowPopupActive = true;
		this.togglePollingStates(false);
		if (authType) {
			this.authType = authType;
		}
	}

	@action.bound
	activateQrCodePopup() {
		this.isBankIdQrCodePopupActive = true;
		this.togglePollingStates(false);
	}

	@action.bound
	deactivateFlowPopup() {
		this.isBankIdFlowPopupActive = false;
	}

	@action.bound
	deactivateQrCodePopup() {
		this.togglePollingStates(true);
		this.onDisposeCapacitor();
	}

	@action.bound
	togglePollingStates(pollingState: boolean) {
		this.shouldStopQrPolling = pollingState;
		this.shouldStopSessionPolling = pollingState;
	}

	@action.bound
	async runSameDeviceFlow() {
		try {
			this.deactivateFlowPopup();
			const apiAuthResponse = await this.getApiAuthentication();
			if (apiAuthResponse?.autoStartToken) {
				await this.redirectToBankId(apiAuthResponse.autoStartToken);
			}
		} catch (e) {
			console.error(e);
		}
	}

	@action.bound
	async runDifferentDeviceFlow() {
		this.deactivateFlowPopup();
		this.activateQrCodePopup();

		try {
			await this.getInitialQrCodeData();

			await this.getRefreshedQrCode();

			const pingResponse = await this.pingForSessionStatus();

			if (pingResponse) {
				if (this.authType == "login") {
					this.rootStore.userAuthStore.loginStore.handleLoginSubmit(
						{ sessionId: pingResponse.sessionId },
						LoginType.BANKID
					);
					this.onDisposeCapacitor();
				}

				if (this.authType == "register") {
					this.assignRegisterData(pingResponse.sessionId);
					this.onDisposeCapacitor();
				}
			}
		} catch (error) {
			console.error(error);
			this.rootStore.notificationStore.error(
				localizationService.t(
					"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID"
				)
			);
		}
	}

	@action.bound
	async getApiAuthentication() {
		this.loaderStore.suspend();
		const successRedirectUrl = getRedirectOriginUrl(
			window.location.pathname,
			true
		);
		const failRedirectUrl = getRedirectOriginUrl(
			window.location.pathname + "?failedAuth=true",
			true
		);
		try {
			const response = await BankIdApiService.bankIdApiAuth(
				successRedirectUrl,
				failRedirectUrl
			);

			if (response?.requestId) {
				this.addTokenToLocalStorage(response.requestId);
				this.setTokenTimeout();
				return response;
			}

			return {} as BankIdApiAuthDto;
		} catch (e) {
			console.error(e);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async getInitialQrCodeData() {
		this.loaderStore.suspend();
		try {
			const qrCodeData = await BankIdMvcService.getInitialQrCodeData();

			if (!qrCodeData) {
				console.error("No QR code data fetched");
				return;
			}

			if (qrCodeData.requestId) {
				this.addTokenToLocalStorage(qrCodeData.requestId);
			}

			this.qrCodeData = qrCodeData;
			return;
		} catch (error) {
			console.error(error);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async getRefreshedQrCode() {
		const startTime = Date.now();

		return new Promise<string>(async (resolve) => {
			const getQrCode = async () => {
				if (this.shouldStopQrPolling) return;

				const elapsedTime = Date.now() - startTime;

				try {
					if (!this.qrCodeData?.qrId) {
						console.error("No QR id inside QR code data");
						this.shouldStopQrPolling = true;
						resolve("");
						return;
					}

					const qrCodeImage =
						await BankIdMvcService.getRefreshedQrCode(
							this.qrCodeData?.qrId
						);

					if (qrCodeImage) {
						this.qrCodeImage = await qrCodeImage.text();
						resolve("");
					}

					if (elapsedTime >= this.maxPoolingDuration) {
						console.log(
							"Maximum polling duration reached. Stopping polling."
						);
						this.shouldStopQrPolling = true;
						resolve("");
						return;
					}

					if (!this.shouldStopQrPolling) {
						setTimeout(getQrCode, this.sessionPollingInterval);
					}
				} catch (e) {
					console.error("Error while checking session status:", e);

					if (elapsedTime >= this.maxPoolingDuration) {
						console.error(
							"Maximum polling duration reached. Stopping polling due to error."
						);
						resolve("");
						this.shouldStopQrPolling = true;
						return;
					}

					if (!this.shouldStopQrPolling) {
						setTimeout(getQrCode, this.sessionPollingInterval);
					}
				}
			};

			await getQrCode();
		});
	}

	@action.bound
	async redirectToBankId(autoStartToken: string) {
		const redirectUrl = `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
		window.location.href = redirectUrl;

		App.addListener("appStateChange", async (state) => {
			if (state.isActive) {
				const requestId = this.getRequestIdFromLocalStorage();

				if (!requestId) {
					return;
				}

				this.setIsAuthPending(true);
				try {
					const pingResponse = await this.pingForSessionStatus();

					if (pingResponse) {
						if (this.authType == "login") {
							this.rootStore.userAuthStore.loginStore.handleLoginSubmit(
								{ sessionId: pingResponse.sessionId },
								LoginType.BANKID
							);
							this.onDisposeCapacitor();
						}

						if (this.authType == "register") {
							this.assignRegisterData(pingResponse.sessionId);
							this.onDisposeCapacitor();
						}
					}
				} catch (error) {
					console.error(error);
					this.rootStore.notificationStore.error(
						localizationService.t(
							"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID"
						)
					);
					this.setIsAuthPending(false);
				} finally {
					this.setIsAuthPending(false);
				}
			}
		});
	}

	@action.bound
	async assignRegisterData(sessionId: string) {
		try {
			this.loaderStore.suspend();

			const response = await BankIdApiService.getUserAdditionalInfo({
				sessionId,
			});

			let formData: BankIdFormData = {};

			if (response.firstName && response.lastName) {
				formData.firstName = response.firstName;
				formData.lastName = response.lastName;
			}

			if (response.pinNumber) {
				formData.personalIdentificator = response.pinNumber;
			}

			if (response.birthDate) {
				const datetime = DateTime.fromISO(response.birthDate);

				formData.day = datetime.get("day").toString();
				formData.month = datetime.get("month").toString();
				formData.year = datetime.get("year").toString();
			}
			runInAction(() => {
				this.capacitorFormData = formData;

				sessionStorage.setItem(
					"additionalBankIdUserInfo",
					JSON.stringify(formData)
				);

				sessionStorage.setItem(
					"arePrefilledFieldsDisabled",
					JSON.stringify(true)
				);

				console.log("prefilled session state set successfully!");
			});
		} catch (err) {
			console.error(err);
			let errorMessage: string;
			switch (err.data.errorCode) {
				case 400921:
					errorMessage =
						"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID";
					break;
				case 400922:
					errorMessage =
						"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_FAILED";
					break;
				default:
					errorMessage = "registration:GENERIC_ERROR";
					break;
			}
			this.rootStore.notificationStore.error(
				localizationService.t(errorMessage)
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async pingForSessionStatus(): Promise<BankIdSessionStatusDto> {
		const startTime = Date.now();

		return new Promise<BankIdSessionStatusDto>(async (resolve) => {
			const checkStatus = async () => {
				if (this.shouldStopSessionPolling) return;

				const elapsedTime = Date.now() - startTime;

				try {
					const requestId = this.getRequestIdFromLocalStorage();

					if (!requestId) {
						console.error("No request id inside local storage!");

						this.togglePollingStates(true);
						resolve({ id: "", sessionId: "", status: 0 });
						return;
					}

					const isLogin = this.authType == "login" ? true : false;

					const response = await BankIdApiService.getSessionStatus(
						requestId,
						isLogin
					);

					if (response.status === BankIdSessionStatus.Finished) {
						this.togglePollingStates(true);
						resolve({
							id: response.id,
							sessionId: response.sessionId,
							status: BankIdSessionStatus.Finished,
						});
						return;
					}

					if (response.status === BankIdSessionStatus.Invalid) {
						this.togglePollingStates(true);
						resolve({
							id: response.id,
							sessionId: response.sessionId,
							status: BankIdSessionStatus.Invalid,
						});
						return;
					}
					if (response.status === BankIdSessionStatus.Failed) {
						this.togglePollingStates(true);
						resolve({
							id: response.id,
							sessionId: response.sessionId,
							status: BankIdSessionStatus.Failed,
						});
						return;
					}

					if (elapsedTime >= this.maxPoolingDuration) {
						console.log(
							"Maximum polling duration reached. Stopping polling."
						);

						this.togglePollingStates(true);
						resolve({ id: "", sessionId: "", status: 0 });
						return;
					}

					if (!this.shouldStopSessionPolling) {
						setTimeout(checkStatus, this.sessionPollingInterval);
					}
				} catch (e) {
					console.error("Error while checking session status:", e);

					if (elapsedTime >= this.maxPoolingDuration) {
						console.log(
							"Maximum polling duration reached. Stopping polling due to error."
						);

						resolve({ id: "", sessionId: "", status: 0 });
						this.togglePollingStates(true);
						return;
					}

					if (!this.shouldStopSessionPolling) {
						setTimeout(checkStatus, this.sessionPollingInterval);
					}
				}
			};

			await checkStatus();
		});
	}

	@action.bound
	addTokenToLocalStorage(requestId: string) {
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.REQUEST_ID,
			requestId
		);
	}

	@action.bound
	getRequestIdFromLocalStorage(): string {
		const requestId = JSON.stringify(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.REQUEST_ID
			)
		);

		if (!requestId) {
			console.error("There is no requestId inside local storage!");
		}

		return JSON.parse(requestId);
	}

	@action.bound
	removePrefilledStateFromSessionStorage() {
		sessionStorage.removeItem("arePrefilledFieldsDisabled");
		sessionStorage.removeItem("additionalBankIdUserInfo");
	}

	@action.bound
	cancelAuth() {
		this.onDisposeCapacitor();
		this.deactivateFlowPopup();
		this.deactivateQrCodePopup();
		this.setIsAuthPending(false);
		this.loaderStore.resume();
	}

	@action.bound
	setTokenTimeout() {
		setTimeout(() => {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.REQUEST_ID
			);
		}, this.tokenTimeout);
	}

	@action.bound
	onDisposeCapacitor() {
		this.authType = "";
		this.isBankIdFlowPopupActive = false;
		this.isBankIdQrCodePopupActive = false;
		this.capacitorFormData = null;
		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.REQUEST_ID
		);
		this.qrCodeData = null;
		this.qrCodeImage = null;
		this.setIsAuthPending(false);

		sessionStorage.removeItem("arePrefilledFieldsDisabled");
		sessionStorage.removeItem("additionalBankIdUserInfo");
	}
}
